import React, { useEffect, useState } from "react";

import { FetchResult } from "@apollo/client";
import {
  AddCircleOutline,
  FileCopyOutlined,
  RemoveCircleOutline
} from "@material-ui/icons";
import { Popconfirm, Popover, Tooltip } from "antd";

import { useUser } from "hooks";

import { DASHBOARD_TYPES, Dashboard, Workspace } from "models/workspace";

import { Delete, IconSpinner } from "components/icons";
import {
  ActionButton,
  Actions,
  DashboardRemoveIcon
} from "components/user-settings/WorkspacesList";
import { useUserContext } from "components/user/context";

import { CloneForm } from "./CloneForm";
import { useCloneForm } from "./hooks/useCloneForm";

export const DASHBOARD_TABLE_ACTION_ICON_SIZE = 20;

export type DashboardTableActionsPropsT = {
  dashboard: Dashboard;
  workspace?: Workspace;
  hideDashboard?: () => Promise<FetchResult>;
  addDashboard?: () => Promise<FetchResult>;
  deleteDashboard?: () => Promise<FetchResult>;
  cloneDashboard?: (title: string, type: string) => Promise<void>;
};

export const DashboardTableActions = (props: DashboardTableActionsPropsT) => {
  const {
    dashboard,
    workspace,
    hideDashboard,
    addDashboard,
    deleteDashboard,
    cloneDashboard
  } = props;
  const [isAddLoading, setIsAddLoading] = useState(false);
  const [showConfirmDeleteOrg, setShowConfirmDeleteOrg] = useState(false);
  const [userContext] = useUserContext();
  const { isAtLeastPowerUser } = useUser();

  const cloneForm = useCloneForm({
    dashboard,
    workspace,
    cloneDashboard,
    isAtLeastPowerUser
  });

  const handleFirstConfirmDelete = () => {
    if (dashboard.type === DASHBOARD_TYPES.Organization) {
      setShowConfirmDeleteOrg(true);
    } else {
      deleteDashboard?.();
    }
  };

  // Check if user can delete this specific dashboard
  const canUserDeleteThisDashboard = () => {
    if (!deleteDashboard) {
      return false;
    }

    // System dashboards cannot be deleted by anyone
    if (dashboard.type === DASHBOARD_TYPES.System) {
      return false;
    }

    // Standard users cannot delete Organization dashboards
    // They can only delete Personal dashboards
    if (
      !userContext?.isAtLeastPowerUser &&
      dashboard.type === DASHBOARD_TYPES.Organization
    ) {
      return false;
    }

    // Power users and above can delete Organization and Personal dashboards
    // Standard users can delete Personal dashboards
    return true;
  };

  // Only show loading spinner for the dashboard being added
  const handleAdd = async (evt: React.MouseEvent) => {
    setIsAddLoading(true);
    await addDashboard?.();

    evt.stopPropagation();
    evt.preventDefault();
  };

  useEffect(() => {
    return () => {
      setIsAddLoading(false);
    };
  }, []);

  return (
    <>
      <Actions $isVisible={isAddLoading}>
        {hideDashboard && (
          <Tooltip
            placement="top"
            title="Hide Dashboard"
            overlayClassName="add-dashboard-tooltip-root">
            <ActionButton $danger>
              <Popconfirm
                className="add-dashboard_popconfirm"
                overlayClassName="add-dashboard_popconfirm"
                onConfirm={hideDashboard}
                onCancel={(evt) => {
                  evt.stopPropagation();
                  evt.preventDefault();
                }}
                okText="Hide"
                okType="danger"
                title={
                  <div>{`Are you sure you want to hide the dashboard "${dashboard.title}" from the workspace?`}</div>
                }>
                <DashboardRemoveIcon>
                  <RemoveCircleOutline
                    style={{ fontSize: DASHBOARD_TABLE_ACTION_ICON_SIZE }}
                    fontSize="inherit"
                  />
                </DashboardRemoveIcon>
              </Popconfirm>
            </ActionButton>
          </Tooltip>
        )}
        {addDashboard && (
          <Tooltip
            placement="top"
            title="Add Dashboard"
            data-testid="add-dashboard-add-button"
            overlayClassName="add-dashboard-tooltip-root">
            <ActionButton
              data-testid="add-dashboard-add-button"
              onClick={handleAdd}
              className="dashboard-table-spinner"
              disabled={isAddLoading}>
              {isAddLoading ? (
                <IconSpinner size={10} />
              ) : (
                <AddCircleOutline
                  style={{ fontSize: DASHBOARD_TABLE_ACTION_ICON_SIZE }}
                  fontSize="inherit"
                />
              )}
            </ActionButton>
          </Tooltip>
        )}
        {cloneDashboard && cloneForm.formProps && (
          <Tooltip
            placement="top"
            title={
              !cloneForm.isPopoverVisible && cloneForm.canShowTooltip
                ? "Clone Dashboard"
                : ""
            }
            overlayClassName="add-dashboard-tooltip-root"
            open={
              !cloneForm.isPopoverVisible && cloneForm.canShowTooltip ? undefined : false
            }>
            <ActionButton onMouseEnter={cloneForm.handleMouseEnter}>
              <Popover
                open={cloneForm.isPopoverVisible}
                onOpenChange={(visible) => {
                  cloneForm.setIsPopoverVisible(visible);
                  if (visible) {
                    cloneForm.handleOpenPopover();
                  }
                }}
                trigger={["click"]}
                placement="top"
                content={<CloneForm {...cloneForm.formProps} />}>
                <div data-testid="add-dashboard-clone-button">
                  <FileCopyOutlined
                    style={{ fontSize: DASHBOARD_TABLE_ACTION_ICON_SIZE }}
                    fontSize="inherit"
                  />
                </div>
              </Popover>
            </ActionButton>
          </Tooltip>
        )}
        {canUserDeleteThisDashboard() && (
          <Tooltip
            placement="top"
            title="Delete Dashboard"
            overlayClassName="add-dashboard-tooltip-root">
            <ActionButton $danger data-testid="add-dashboard-delete-button">
              <Popconfirm
                className="add-dashboard_popconfirm"
                overlayClassName="add-dashboard_popconfirm"
                onConfirm={handleFirstConfirmDelete}
                onCancel={(evt) => {
                  evt.stopPropagation();
                  evt.preventDefault();
                }}
                okText="Delete"
                okType="danger"
                title={
                  <div>{`Are you sure you want to delete the dashboard "${dashboard.title}"?`}</div>
                }>
                <DashboardRemoveIcon>
                  <Delete size={DASHBOARD_TABLE_ACTION_ICON_SIZE} />
                </DashboardRemoveIcon>
              </Popconfirm>
            </ActionButton>
          </Tooltip>
        )}
      </Actions>

      <Popconfirm
        className="add-dashboard_popconfirm"
        overlayClassName="add-dashboard_popconfirm"
        onConfirm={() => {
          deleteDashboard?.();
          setShowConfirmDeleteOrg(false);
        }}
        onCancel={(evt) => {
          setShowConfirmDeleteOrg(false);
          evt?.stopPropagation();
          evt?.preventDefault();
        }}
        open={showConfirmDeleteOrg}
        onOpenChange={(visible) => setShowConfirmDeleteOrg(visible)}
        okText="Delete for All Users"
        okType="danger"
        title={
          <div>
            This will delete the dashboard for all users in your organization. Continue?
          </div>
        }>
        <div />
      </Popconfirm>
    </>
  );
};
